import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { State } from '../../shared/core/state';

import * as CourseActions from '../../shared/core/state/course/course.actions';
import * as AppConfigActions from '../../shared/core/state/config/config.action';
import {
  CourseItem,
  CourseResource,
} from '../../shared/core/state/course/models';
import { filter, first } from 'rxjs';

import { OrderService } from '../../shared/core/state/order/order.service';
import { PendingCourseOrder } from '../../shared/core/state/order/order.model';

export interface FlatResourceItem {
  resource: CourseResource;
  sectionTopic: string;
  sectionIndex: number;
}

@Component({
  selector: 'black-playlist',
  templateUrl: './playlist.component.html',
  styleUrls: ['./playlist.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class PlaylistComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private store = inject<Store<State>>(Store);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private orderService = inject(OrderService);

  courseId: string | null = null;
  selectedCourse: CourseItem | null = null;
  pendingOrder: PendingCourseOrder | null = null;
  activeResourceId = '';
  activeLessonTitle = '';
  activeSectionTopic = '';
  isSidebarOpen = true;
  activeSectionIndex = 0;
  isBannerDismissed = false;

  allFlatResources: FlatResourceItem[] = [];

  constructor() {
    this.courseId = this.route.snapshot.paramMap.get('id');

    if (isPlatformBrowser(this.platformId) && window.innerWidth < 1024) {
      this.isSidebarOpen = false;
    }

    this.store
      .pipe(
        select((store) => store.courses.selectedCourseItem),
        first((item) => item != null),
      )
      .subscribe({
        next: (item) => {
          this.selectedCourse = item;
          this.buildFlatResources();
        },
        complete: () => {
          this.syncActiveFromRoute();
        },
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.syncActiveFromRoute();
      });
  }

  ngOnInit(): void {
    this.store.dispatch(AppConfigActions.hideFooter());
    if (this.courseId) {
      if (isPlatformBrowser(this.platformId)) {
        this.isBannerDismissed = sessionStorage.getItem(`bse_dismiss_banner_${this.courseId}`) === 'true';
      }
      this.store.dispatch(
        CourseActions.selectCourseItem({
          id: this.courseId,
        }),
      );
      this.orderService.getPendingCourseOrders().subscribe({
        next: (map) => {
          if (this.courseId && map.has(this.courseId)) {
            this.pendingOrder = map.get(this.courseId) || null;
          }
        },
      });
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(AppConfigActions.showFooter());
  }

  private buildFlatResources() {
    this.allFlatResources = [];
    if (!this.selectedCourse?.contents) return;

    this.selectedCourse.contents.forEach((content, sIdx) => {
      (content.resources || []).forEach((res) => {
        this.allFlatResources.push({
          resource: res,
          sectionTopic: content.topic,
          sectionIndex: sIdx,
        });
      });
    });
  }

  getTargetRef(resource: CourseResource): string {
    const res = resource.resource;
    const resId =
      typeof res === 'object' && res
        ? (res as any)._id || (res as any).path
        : res;
    return String(resId || resource.url || '').trim();
  }

  private syncActiveFromRoute() {
    const childParam = this.route.firstChild?.snapshot.paramMap.get('id');
    if (childParam && this.allFlatResources.length > 0) {
      const decoded = decodeURIComponent(childParam);
      const match = this.allFlatResources.find(
        (f) =>
          f.resource._id === decoded ||
          this.getTargetRef(f.resource) === decoded ||
          f.resource.url === decoded,
      );
      if (match) {
        this.activeResourceId = match.resource._id;
        this.activeLessonTitle = match.resource.title;
        this.activeSectionTopic = match.sectionTopic;
        this.activeSectionIndex = match.sectionIndex;
        return;
      }
    }

    if (!this.activeResourceId && this.allFlatResources.length > 0) {
      this.changeCurrentVideo(this.allFlatResources[0].resource);
    }
  }

  get currentIndex(): number {
    return this.allFlatResources.findIndex(
      (r) => r.resource._id === this.activeResourceId,
    );
  }

  get hasPrev(): boolean {
    return this.currentIndex > 0;
  }

  get hasNext(): boolean {
    return (
      this.currentIndex >= 0 &&
      this.currentIndex < this.allFlatResources.length - 1
    );
  }

  get isSuspendedOverdue(): boolean {
    return this.pendingOrder?.status === 'OVERDUE';
  }

  get isInGracePeriod(): boolean {
    return (
      this.pendingOrder?.status === 'PENDING' &&
      !!this.pendingOrder?.gracePeriodEnd
    );
  }

  get graceDaysRemaining(): number {
    if (!this.pendingOrder?.gracePeriodEnd) return 0;
    return Math.max(0, Math.ceil((new Date(this.pendingOrder.gracePeriodEnd).getTime() - Date.now()) / 86400000));
  }

  get isPendingApproval(): boolean {
    return this.pendingOrder?.status === 'AWAITING_APPROVAL';
  }

  viewOrders(): void {
    this.router.navigate(['/dashboard/orders']);
  }

  payInstallment(): void {
    if (this.pendingOrder?.orderId) {
      this.router.navigate(['/order/payment'], {
        queryParams: { orderId: this.pendingOrder.orderId },
      });
    }
  }

  dismissBanner(): void {
    this.isBannerDismissed = true;
    if (isPlatformBrowser(this.platformId) && this.courseId) {
      sessionStorage.setItem(`bse_dismiss_banner_${this.courseId}`, 'true');
    }
  }

  goToPrev() {
    if (this.isSuspendedOverdue) return;
    if (this.hasPrev) {
      this.changeCurrentVideo(
        this.allFlatResources[this.currentIndex - 1].resource,
      );
    }
  }

  goToNext() {
    if (this.isSuspendedOverdue) return;
    if (this.hasNext) {
      this.changeCurrentVideo(
        this.allFlatResources[this.currentIndex + 1].resource,
      );
    }
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  toggleSection(index: number) {
    this.activeSectionIndex = this.activeSectionIndex === index ? -1 : index;
  }

  getResourceType(resource: CourseResource): 'video' | 'document' | 'link' {
    const t = (resource.type || '').toLowerCase();
    if (t === 'video') return 'video';
    if (t === 'document' || t === 'pdf' || /\.pdf($|[?#])/i.test(resource.url || '')) return 'document';
    if (t === 'link' || t === 'external' || (/^https?:\/\//i.test(resource.url || '') && !/youtu|vimeo|drive\.google|\.(mp4|webm|m3u8|mpd)($|[?#])/i.test(resource.url || ''))) return 'link';
    return 'video';
  }

  changeCurrentVideo(resource: CourseResource) {
    if (this.isSuspendedOverdue) return;
    this.activeResourceId = resource._id;
    this.activeLessonTitle = resource.title;
    const flat = this.allFlatResources.find(
      (f) => f.resource._id === resource._id,
    );
    if (flat) {
      this.activeSectionTopic = flat.sectionTopic;
      this.activeSectionIndex = flat.sectionIndex;
    }

    // Auto-collapse content sidebar on mobile after selecting a video
    if (isPlatformBrowser(this.platformId) && window.innerWidth < 1024) {
      this.isSidebarOpen = false;
    }

    const type = this.getResourceType(resource);
    const target = this.getTargetRef(resource);

    // If external web link, open link in new tab
    if (
      type === 'link' &&
      target &&
      (target.startsWith('http://') || target.startsWith('https://'))
    ) {
      if (isPlatformBrowser(this.platformId)) {
        window.open(target, '_blank');
      }
    }

    if (target && this.courseId) {
      const isUrl =
        target.startsWith('http://') || target.startsWith('https://');
      this.router.navigate([
        'courses',
        this.courseId,
        'playlist',
        'video',
        isUrl ? encodeURIComponent(target) : target,
      ]);
    }
  }
}
